import ApiKeyRole from "enums/ApiKeyRole";
import type {
  ApiKeyPermissionGroup,
  ApiKeyPermissionOption,
  ApiKeyPermissionPreset,
} from "hooks/useApiKeys";

export type GroupState = "all" | "some" | "none";

export const CUSTOM_ACCESS = "custom";

/** Permission names carry a colon, which a translation key may not. */
export const apiKeyPermissionHelpKey = (name: string): string =>
  `ApiKeyPermission_${name.replace(/[^A-Za-z0-9]/g, "_")}`;

export const apiKeyPresetLabelKey = (id: string): string =>
  `ApiKeyPreset_${id.replace(/[^A-Za-z0-9]/g, "_")}`;

export const apiKeyPresetCaptionKey = (id: string): string =>
  `ApiKeyPresetCaption_${id.replace(/[^A-Za-z0-9]/g, "_")}`;

export const apiKeyGroupLabelKey = (id: string): string =>
  `ApiKeyGroup_${id.replace(/[^A-Za-z0-9]/g, "_")}`;

const sortUnique = (names: string[]): string[] =>
  Array.from(new Set(names)).sort();

const findOption = (
  name: string,
  options: ApiKeyPermissionOption[],
): ApiKeyPermissionOption | undefined =>
  options.find((option) => option.name === name);

/** A held permission can always be removed; adding one needs the editor's authority. */
export const canTogglePermission = (
  option: ApiKeyPermissionOption,
  selected: string[],
): boolean => selected.includes(option.name) || option.assignable;

/** Every permission a group holds that the catalogue offers, its sensitive ones last. */
export const getGroupOptions = (
  group: ApiKeyPermissionGroup,
  options: ApiKeyPermissionOption[],
): ApiKeyPermissionOption[] =>
  [...group.permissions, ...group.sensitivePermissions].flatMap((name) =>
    options.filter((option) => option.name === name),
  );

/** What a group's select-all can add: never a sensitive permission, only what the editor may add. */
export const getGroupPermissions = (
  group: ApiKeyPermissionGroup,
  options: ApiKeyPermissionOption[],
): string[] =>
  group.permissions.filter((name) => {
    const option = findOption(name, options);
    return !!option && !option.sensitive && option.assignable;
  });

export const getGroupState = (
  groupPermissions: string[],
  selected: string[],
): GroupState => {
  const held = groupPermissions.filter((name) =>
    selected.includes(name),
  ).length;
  if (held === 0) return "none";
  return held === groupPermissions.length ? "all" : "some";
};

export const toggleGroup = (
  groupPermissions: string[],
  selected: string[],
): string[] =>
  getGroupState(groupPermissions, selected) === "all"
    ? selected.filter((name) => !groupPermissions.includes(name))
    : sortUnique([...selected, ...groupPermissions]);

export const togglePermission = (name: string, selected: string[]): string[] =>
  selected.includes(name)
    ? selected.filter((held) => held !== name)
    : sortUnique([...selected, name]);

/** The permissions a preset grants that the catalogue offers here. */
export const getPresetPermissions = (
  preset: ApiKeyPermissionPreset,
  options: ApiKeyPermissionOption[],
): string[] =>
  sortUnique(preset.permissions.filter((name) => !!findOption(name, options)));

/** A preset is offered only when the editor may add every permission in it. */
export const canChoosePreset = (
  preset: ApiKeyPermissionPreset,
  options: ApiKeyPermissionOption[],
): boolean =>
  getPresetPermissions(preset, options).every(
    (name) => findOption(name, options)?.assignable,
  );

/** The preset whose permissions are exactly the selection, or Custom. */
export const getAccessChoice = (
  selected: string[],
  presets: ApiKeyPermissionPreset[],
  options: ApiKeyPermissionOption[],
): string =>
  presets.find((preset) =>
    isSamePermissionSet(getPresetPermissions(preset, options), selected),
  )?.id ?? CUSTOM_ACCESS;

/** A key holding only what a Read Only key may hold is Read Only; anything else needs Admin. */
export const getKeyRole = (
  selected: string[],
  options: ApiKeyPermissionOption[],
): ApiKeyRole =>
  selected.every((name) =>
    findOption(name, options)?.roles.includes(ApiKeyRole.ReadOnly),
  )
    ? ApiKeyRole.ReadOnly
    : ApiKeyRole.Admin;

export const countSensitive = (
  selected: string[],
  options: ApiKeyPermissionOption[],
): number =>
  selected.filter((name) => findOption(name, options)?.sensitive).length;

/** Keeps only permissions the catalogue offers, as the server would require. */
export const prunePermissions = (
  selected: string[],
  options: ApiKeyPermissionOption[],
): string[] =>
  sortUnique(selected.filter((name) => !!findOption(name, options)));

export const isSamePermissionSet = (left: string[], right: string[]): boolean =>
  left.length === right.length && left.every((name) => right.includes(name));
